import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import Scrollbar from '../scrollbar';
import { 
  getAssignmentStatusColor, 
  getAssignmentStatusText, 
  formatAssignmentDate,
  canReturnAssignment 
} from '../../utils/inventoryAssignmentUtils';

const TABLE_HEAD = [
  { id: 'assigned_to', label: 'Employee' },
  { id: 'inventory_item', label: 'Item' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'assigned_date', label: 'Assigned Date' },
  { id: 'return_date', label: 'Return Date' },
  { id: 'status', label: 'Status' },
  { id: 'remarks', label: 'Remarks' },
  { id: 'actions', label: 'Actions', alignRight: true },
];

const InventoryAssignmentTable = ({
  inventoryAssignments,
  isLoading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onViewAssignment,
  onEditAssignment,
  onDeleteAssignment,
  onReturnAssignment,
}) => (
    <Card>
      <Scrollbar>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map((headCell) => (
                  <TableCell key={headCell.id} align={headCell.alignRight ? 'right' : 'left'}>
                    {headCell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : inventoryAssignments?.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                    <Paper sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2">No inventory assignments found.</Typography>
                    </Paper>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {inventoryAssignments.map((assignment) => (
                  <TableRow hover key={assignment.id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {assignment.assigned_to?.full_name || assignment.assigned_to?.name || 'Unknown Employee'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {assignment.inventory_item?.name || 'Unknown Item'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {assignment.quantity || 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatAssignmentDate(assignment.assigned_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatAssignmentDate(assignment.return_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getAssignmentStatusText(assignment.status)}
                        color={getAssignmentStatusColor(assignment.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                        {assignment.remarks || 'No remarks'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton
                          color="primary"
                          onClick={() => onViewAssignment(assignment)}
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => onEditAssignment(assignment)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => onDeleteAssignment(assignment)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                        {canReturnAssignment(assignment) && (
                          <Button
                            color="success"
                            onClick={() => onReturnAssignment(assignment)}
                            size="small"
                            variant="outlined"
                            startIcon={<AssignmentReturnIcon />}
                            sx={{ minWidth: 'auto', px: 1 }}
                          >
                            Return
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Card>
);

InventoryAssignmentTable.propTypes = {
  inventoryAssignments: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onViewAssignment: PropTypes.func.isRequired,
  onEditAssignment: PropTypes.func.isRequired,
  onDeleteAssignment: PropTypes.func.isRequired,
  onReturnAssignment: PropTypes.func.isRequired,
};

export default InventoryAssignmentTable;